/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import TextareaAutosize from 'react-autosize-textarea';
import _ from 'lodash';
import clsx from 'clsx';

import BaseComponent from '../BaseComponent';

interface Props<T> {
  autoCapitalize?: string;
  autoComplete: string;
  autoFocus: boolean;
  autoCorrect?: string;
  checked?: boolean;
  classNames?: string;
  'data-testid'?: string;
  disabled?: boolean;
  enableKeyboardShorcuts: boolean;
  expand?: boolean;
  field: ReduxFormField<T>;
  filter?: InputFilter;
  formNoValidate?: boolean;
  id?: string;
  maxLength?: number;
  numRows?: number;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
  onFocus?(event: React.FocusEvent<HTMLInputElement>): void;
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void;
  onKeyPress?(event: React.KeyboardEvent<HTMLInputElement>): void;
  options?: SelectOption[];
  pattern?: string;
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'aria-activedescendant'?: string;
  ariaLabel?: string;
  placeholder?: string;
  readOnlyValue?: string;
  required?: boolean;
  role?: string;
  tabIndex?: number;
  tooltip?: string;
  readOnly?: boolean;
  type: string;
  value?: string;
}

interface State {
  viewValue: string;
}

/**
 * @deprecated
 * Please see the inputs in the ui folder of your project
 */
export default class Input<T = string | number> extends BaseComponent<Props<T>, State> {
  static defaultProps = {
    autoFocus: false,
    autoComplete: 'invalid-off',
    enableKeyboardShorcuts: false,
    type: 'text',
  };

  input = React.createRef<HTMLInputElement>();

  state = {
    viewValue: '',
  };

  static getDerivedStateFromProps(props: Props<any>, state: State) {
    if (props.field.active || !props.filter) {
      return state;
    }

    return {
      ...state,
      viewValue: props.filter.formatModelValue(props.field.value),
    };
  }

  constructor(props: Props<T>) {
    super(props);

    if (props.filter) {
      this.state = {
        viewValue: props.filter.formatModelValue(props.field.value as any) as any,
      };
    }
  }

  handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    const viewValue = event.target.value;
    let modelValue: any = event;

    if (this.props.filter) {
      modelValue = this.props.filter.parseViewValue(viewValue);
      const newViewValue = this.props.filter.formatModelValue(modelValue);

      this.setState({
        viewValue: newViewValue as string,
      });
    }

    this.props.field.onBlur(modelValue);

    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  }

  handleFocus(eventOrValue: React.FocusEvent<HTMLInputElement>) {
    if (this.props.onFocus) {
      this.props.onFocus(eventOrValue);
    }
  }

  handleKeyPress(eventOrValue: React.KeyboardEvent<HTMLInputElement>) {
    const { onKeyPress } = this.props;
    if (onKeyPress) {
      onKeyPress(eventOrValue);
    }
  }

  handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const viewValue = event.target.value;
    // this is necessary because of event pooling. we're sending the event below, and if you're not using redux form field it breaks.
    event.persist();
    let modelValue: any = event;

    if (this.props.filter) {
      if (this.props.filter.formatOnChange) {
        modelValue = this.props.filter.parseViewValue(viewValue);
        const newViewValue = this.props.filter.formatModelValue(modelValue);
        this.setState({ viewValue: newViewValue as string });
      } else {
        this.setState({ viewValue });
      }
    }

    this.props.field.onChange(modelValue);

    if (this.props.onChange) {
      this.props.onChange(event);
    }
  }

  renderOptions() {
    return _.map(this.props.options, (option: SelectOption) => {
      if (!option) {
        throw new Error(
          `Select with name ${this.props.field.name || '???'} received undefined option.`
        );
      }
      return (
        <option
          key={option.key}
          data-testid={option.value}
          disabled={option.disabled}
          value={option.value}
        >
          {option.label}
        </option>
      );
    });
  }

  render() {
    const { classNames, enableKeyboardShorcuts, field, type, readOnly } = this.props;

    const hasError = !!field && field.touched && (field.invalid || !!field.error);
    const inputClassNames = clsx(
      'Input',
      {
        'has-errors': hasError,
        'is-valid': field && field.dirty && !field.error,
        mousetrap: enableKeyboardShorcuts,
      },
      classNames
    );

    // Use the spread operator to pull off the props that do not apply to the
    // HTML elements used below (select, input, textarea). MH - 8.23.16
    // tslint:disable:variable-name
    const newField: any = {
      ...field,
    };
    delete newField.initialValue;
    delete newField.autofill;
    delete newField.onUpdate;
    delete newField.valid;
    delete newField.invalid;
    delete newField.dirty;
    delete newField.pristine;
    delete newField.active;
    delete newField.touched;
    delete newField.visited;
    delete newField.autofilled;
    delete newField.error;

    const ariaProps = {
      'aria-label': this.props.ariaLabel ?? this.props.placeholder,
      'aria-invalid': hasError,
      'aria-describedby': field.name && hasError ? 'error_' + field.name : undefined,
    };

    // tslint:enable:variable-name

    if (type === 'radio' || type === 'checkbox') {
      return (
        <input
          className={inputClassNames}
          data-testid={this.props['data-testid']}
          disabled={this.props.disabled}
          id={this.props.id}
          type={type}
          readOnly={readOnly}
          name={this.props.field.name}
          {...newField}
          {...ariaProps}
          tabIndex={this.props.tabIndex}
          value={this.props.value}
          checked={this.props.checked}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
        />
      );
    } else if (type === 'select') {
      return (
        <select
          id={this.props.id}
          className={inputClassNames}
          data-testid={this.props['data-testid']}
          readOnly={readOnly}
          {...newField}
          {...ariaProps}
          disabled={this.props.disabled}
          tabIndex={this.props.tabIndex}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
        >
          {this.renderOptions()}
        </select>
      );
    } else if (type === 'textarea') {
      const props = {
        className: inputClassNames,
        'data-testid': this.props['data-testid'],
        id: this.props.id,
        placeholder: this.props.placeholder,
        type,
        tabIndex: this.props.tabIndex,
        ...newField,
        ...ariaProps,
        rows: this.props.numRows,
        ref: this.input,
        autoFocus: this.props.autoFocus,
        disabled: this.props.disabled,
        readOnly: this.props.readOnly,
        maxLength: this.props.maxLength,
        onBlur: this.handleBlur,
        onChange: this.handleChange,
        onFocus: this.handleFocus,
        onKeyDown: this.props.onKeyDown,
        onKeyPress: this.handleKeyPress,
      };

      /**
       * tldr; The any cast below is a band-aid for React 18 support.
       *
       * This package is abandoned and the types were generated with React 16.
       * The generated types inlined some attributes from the React 16 types that
       * has been removed in React 18. This makes TS ask for props here that don't
       * do anything/we don't want to set.  The any was a quick and dirty way to
       * handle this. Once we remove the expand use of this component from dashboard
       * we can get remove the library and this component.
       *
       * https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/69006
       */

      if (this.props.expand) {
        return <TextareaAutosize {...(props as any)} {...ariaProps} />;
      }

      return <textarea {...props} {...ariaProps} />;
    } else {
      let viewValue: any = field.value;

      if (this.props.filter) {
        viewValue = this.state.viewValue;
      }

      if (!viewValue && this.props.readOnlyValue) {
        viewValue = this.props.readOnlyValue;
      }

      const maybeProps: any = {};

      if (this.props.role) {
        maybeProps.role = this.props.role;
      }
      if (this.props['aria-expanded'] !== undefined) {
        maybeProps['aria-expanded'] = this.props['aria-expanded'];
      }
      if (this.props['aria-controls']) {
        maybeProps['aria-controls'] = this.props['aria-controls'];
      }
      if (this.props['aria-activedescendant']) {
        maybeProps['aria-activedescendant'] = this.props['aria-activedescendant'];
      }
      // We only do this because autofocus is only used in dashboard

      return (
        <input
          autoCapitalize={this.props.autoCapitalize}
          autoComplete={this.props.autoComplete}
          autoCorrect={this.props.autoCorrect}
          autoFocus={this.props.autoFocus}
          className={inputClassNames}
          data-testid={this.props['data-testid']}
          {...ariaProps}
          disabled={this.props.disabled}
          id={this.props.id}
          maxLength={this.props.maxLength}
          name={field.name}
          formNoValidate={this.props.formNoValidate}
          pattern={this.props.pattern}
          placeholder={this.props.placeholder}
          ref={this.input}
          type={type}
          tabIndex={this.props.tabIndex}
          value={viewValue}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          onKeyDown={this.props.onKeyDown}
          onKeyPress={this.handleKeyPress}
          title={this.props.tooltip}
          readOnly={readOnly}
          {...maybeProps}
        />
      );
    }
  }
}
